import React, { useState } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  Box,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { authService } from "../../utils/authService";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosConfig";
import { useTranslation } from "react-i18next";

export const ClientManagement = () => {
  const { t } = useTranslation();
  const locationRoute = useLocation();
  const navigate = useNavigate();
  const clientToEdit = locationRoute?.state?.client;
  const editMode = locationRoute?.state?.editMode;

  const [client, setClient] = useState(() => {
    if (clientToEdit) {
      return {
        ...clientToEdit,
        locations_attributes: clientToEdit.locations.map((loc) => ({
          id: loc.id,
          name: loc.name,
          address: loc.address,
          phone: loc.phone, // Se agrega phone al mapear las ubicaciones
          buildings_attributes: loc.buildings.map((bld) => ({
            id: bld.id,
            name: bld.name,
            floors: bld.floors,
            elevators_attributes: bld.elevators.map((elv) => ({
              id: elv.id,
              identifier: elv.identifier,
              capacity: elv.capacity,
              status: elv.status,
            })),
          })),
        })),
      };
    } else {
      return {
        name: "",
        email: "",
        phone: "",
        locations_attributes: [
          {
            name: "",
            address: "",
            phone: "", // Nuevo campo para phone en location
            buildings_attributes: [
              {
                name: "",
                floors: "",
                elevators_attributes: [
                  {
                    identifier: "",
                    capacity: "",
                    status: "operational",
                  },
                ],
              },
            ],
          },
        ],
      };
    }
  });

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleLocationAdd = () => {
    setClient({
      ...client,
      locations_attributes: [
        ...client.locations_attributes,
        {
          name: "",
          address: "",
          phone: "", // Agregado phone
          buildings_attributes: [],
        },
      ],
    });
  };

  const handleLocationRemove = (locationIndex) => {
    const updatedLocations = client.locations_attributes.filter(
      (_, index) => index !== locationIndex
    );
    setClient({ ...client, locations_attributes: updatedLocations });
  };

  const handleBuildingAdd = (locationIndex) => {
    const updatedLocations = [...client.locations_attributes];
    updatedLocations[locationIndex].buildings_attributes.push({
      name: "",
      floors: "",
      elevators_attributes: [],
    });
    setClient({ ...client, locations_attributes: updatedLocations });
  };

  const handleBuildingRemove = (locationIndex, buildingIndex) => {
    const updatedLocations = [...client.locations_attributes];
    updatedLocations[locationIndex].buildings_attributes = updatedLocations[
      locationIndex
    ].buildings_attributes.filter((_, index) => index !== buildingIndex);
    setClient({ ...client, locations_attributes: updatedLocations });
  };

  const handleElevatorAdd = (locationIndex, buildingIndex) => {
    const updatedLocations = [...client.locations_attributes];
    updatedLocations[locationIndex].buildings_attributes[
      buildingIndex
    ].elevators_attributes.push({
      identifier: "",
      capacity: "",
      status: "operational",
    });
    setClient({ ...client, locations_attributes: updatedLocations });
  };

  const handleElevatorRemove = (locationIndex, buildingIndex, elevatorIndex) => {
    const updatedLocations = [...client.locations_attributes];
    updatedLocations[locationIndex].buildings_attributes[
      buildingIndex
    ].elevators_attributes = updatedLocations[locationIndex].buildings_attributes[
      buildingIndex
    ].elevators_attributes.filter((_, index) => index !== elevatorIndex);
    setClient({ ...client, locations_attributes: updatedLocations });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = authService.getToken();
      const url = `${process.env.REACT_APP_API_URL}/clients${
        editMode ? `/${client.id}` : ""
      }`;
      const method = editMode ? "PUT" : "POST";

      await axiosInstance({
        method: method,
        url: url,
        data: { client },
      });

      setNotification({
        open: true,
        message: editMode
          ? t("clientUpdatedSuccessfully")
          : t("clientCreatedSuccessfully"),
        severity: "success",
      });
      navigate("/client-list");
    } catch (error) {
      setNotification({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {editMode
            ? t("clientManagementTitleEdit")
            : t("clientManagementTitleCreate")}
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            required
            fullWidth
            label={t("name")}
            margin="normal"
            value={client.name}
            onChange={(e) => setClient({ ...client, name: e.target.value })}
          />
          <TextField
            required
            fullWidth
            label={t("email")}
            type="email"
            margin="normal"
            value={client.email}
            onChange={(e) => setClient({ ...client, email: e.target.value })}
          />
          <TextField
            required
            fullWidth
            label={t("phone")}
            margin="normal"
            value={client.phone}
            onChange={(e) => setClient({ ...client, phone: e.target.value })}
          />

          {client.locations_attributes.map((location, locationIndex) => (
            <Box
              key={locationIndex}
              sx={{
                mt: 4,
                p: 2,
                border: "1px solid #ddd",
                borderRadius: 1,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  {t("locationName")} {locationIndex + 1}
                </Typography>
                <IconButton
                  color="error"
                  onClick={() => handleLocationRemove(locationIndex)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <TextField
                required
                fullWidth
                label={t("locationName")}
                margin="normal"
                value={location.name}
                onChange={(e) => {
                  const updatedLocations = [...client.locations_attributes];
                  updatedLocations[locationIndex].name = e.target.value;
                  setClient({
                    ...client,
                    locations_attributes: updatedLocations,
                  });
                }}
              />
              <TextField
                required
                fullWidth
                label={t("address")}
                margin="normal"
                value={location.address}
                onChange={(e) => {
                  const updatedLocations = [...client.locations_attributes];
                  updatedLocations[locationIndex].address = e.target.value;
                  setClient({
                    ...client,
                    locations_attributes: updatedLocations,
                  });
                }}
              />
              <TextField
                required
                fullWidth
                label={t("phone")}
                margin="normal"
                value={location.phone || ""}
                onChange={(e) => {
                  const updatedLocations = [...client.locations_attributes];
                  updatedLocations[locationIndex].phone = e.target.value;
                  setClient({
                    ...client,
                    locations_attributes: updatedLocations,
                  });
                }}
              />

              {location.buildings_attributes.map((building, buildingIndex) => (
                <Box
                  key={buildingIndex}
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid #eee",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1">
                      {t("buildingName")} {buildingIndex + 1}
                    </Typography>
                    <IconButton
                      color="error"
                      onClick={() =>
                        handleBuildingRemove(locationIndex, buildingIndex)
                      }
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <TextField
                    required
                    fullWidth
                    label={t("buildingName")}
                    margin="normal"
                    value={building.name}
                    onChange={(e) => {
                      const updatedLocations = [...client.locations_attributes];
                      updatedLocations[locationIndex].buildings_attributes[
                        buildingIndex
                      ].name = e.target.value;
                      setClient({
                        ...client,
                        locations_attributes: updatedLocations,
                      });
                    }}
                  />
                  <TextField
                    required
                    fullWidth
                    label={t("numberOfFloors")}
                    type="number"
                    margin="normal"
                    value={building.floors}
                    onChange={(e) => {
                      const updatedLocations = [...client.locations_attributes];
                      updatedLocations[locationIndex].buildings_attributes[
                        buildingIndex
                      ].floors = e.target.value;
                      setClient({
                        ...client,
                        locations_attributes: updatedLocations,
                      });
                    }}
                  />

                  {building.elevators_attributes.map(
                    (elevator, elevatorIndex) => (
                      <Box
                        key={elevatorIndex}
                        sx={{
                          mt: 2,
                          p: 2,
                          border: "1px solid #f5f5f5",
                          borderRadius: 1,
                          backgroundColor: "white",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {t("elevatorIdentifier")} {elevatorIndex + 1}
                          </Typography>
                          <IconButton
                            color="error"
                            onClick={() =>
                              handleElevatorRemove(
                                locationIndex,
                                buildingIndex,
                                elevatorIndex
                              )
                            }
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <TextField
                          required
                          fullWidth
                          label={t("elevatorIdentifier")}
                          margin="normal"
                          value={elevator.identifier}
                          onChange={(e) => {
                            const updatedLocations = [
                              ...client.locations_attributes,
                            ];
                            updatedLocations[locationIndex].buildings_attributes[
                              buildingIndex
                            ].elevators_attributes[elevatorIndex].identifier =
                              e.target.value;
                            setClient({
                              ...client,
                              locations_attributes: updatedLocations,
                            });
                          }}
                        />
                        <TextField
                          required
                          fullWidth
                          label={t("capacityKg")}
                          type="number"
                          margin="normal"
                          value={elevator.capacity}
                          onChange={(e) => {
                            const updatedLocations = [
                              ...client.locations_attributes,
                            ];
                            updatedLocations[locationIndex].buildings_attributes[
                              buildingIndex
                            ].elevators_attributes[elevatorIndex].capacity =
                              e.target.value;
                            setClient({
                              ...client,
                              locations_attributes: updatedLocations,
                            });
                          }}
                        />
                        <Select
                          fullWidth
                          value={elevator.status}
                          onChange={(e) => {
                            const updatedLocations = [
                              ...client.locations_attributes,
                            ];
                            updatedLocations[locationIndex].buildings_attributes[
                              buildingIndex
                            ].elevators_attributes[elevatorIndex].status =
                              e.target.value;
                            setClient({
                              ...client,
                              locations_attributes: updatedLocations,
                            });
                          }}
                          margin="normal"
                          sx={{ mt: 2 }}
                        >
                          <MenuItem value="operational">
                            {t("operational")}
                          </MenuItem>
                          <MenuItem value="maintenance">
                            {t("maintenance")}
                          </MenuItem>
                          <MenuItem value="out_of_service">
                            {t("outOfService")}
                          </MenuItem>
                        </Select>
                      </Box>
                    )
                  )}

                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleElevatorAdd(locationIndex, buildingIndex)}
                    sx={{ mt: 2 }}
                  >
                    {t("addElevator")}
                  </Button>
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                onClick={() => handleBuildingAdd(locationIndex)}
                sx={{ mt: 2 }}
              >
                {t("addBuilding")}
              </Button>
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleLocationAdd}
            sx={{ mt: 2 }}
          >
            {t("addLocation")}
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 4 }}
          >
            {editMode ? t("updateClient") : t("createClient")}
          </Button>
        </form>
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
